import { Button, Icon, Tooltip, Typography, type IDropdownItem, type IButtonProps } from 'shared/ui'
import { Select } from 'shared/ui/Select/Select'
import { SeatPlanIncludesTooltip } from 'pages/settings/ui/BillingPlan/ui/PlanIncludesTooltipContent/SeatPlanIncludesTooltip'
import { NumberPlanIncludesTooltip } from 'pages/settings/ui/BillingPlan/ui/PlanIncludesTooltipContent/NumberPlanIncludesTooltip'
import styles from './styles.module.scss'

export type IPlan = {
  name: string
  icon: 'cube' | 'atom' | 'diamond'
  description: string
  selectItems: IDropdownItem[]
  onSelectValue: (value: string | number, item: IDropdownItem) => void
  buttonProps?: Partial<IButtonProps>
  iconColor: string
  showTooltip?: boolean
  price: string
  suffix: string | null
  showCustomCredits?: boolean
  isCustom?: boolean
}

type IPlanContainerProps = {
  plan: IPlan
  additionalNumberPrice: string
  additionalSeatPrice: string
  selectedValue: string | number
  onSelectValue: (value: string | number, item: IDropdownItem) => void
}

export const PlanContainer = ({
  plan,
  additionalNumberPrice,
  additionalSeatPrice,
  selectedValue,
  onSelectValue,
}: IPlanContainerProps) => {
  return (
    <div className={styles.planContainer}>
      <Icon fontSize={32} icon={plan.icon} color={plan.iconColor} className={styles.planIcon} />
      <Typography ariaLabel='plans_header_title' variant='heading-sm' className={styles.planTitle}>
        {plan.name}
      </Typography>
      <Typography
        ariaLabel='plans_header_description'
        variant='body-md-regular'
        className={styles.planDescription}
      >
        {plan.description}
      </Typography>

      <div className={styles.infoContainer}>
        <div>
          <Typography ariaLabel='plans_header_title' variant='heading-lg'>
            {plan.price}
          </Typography>

          {plan.suffix && (
            <Typography
              className={styles.infoContainer__suffix}
              ariaLabel='plans_header_title'
              variant='body-md-regular'
            >
              {plan.suffix}
            </Typography>
          )}
        </div>

        {plan.showCustomCredits && (
          <Typography
            className={styles.customCredits}
            tag={'div'}
            ariaLabel='custom_credits'
            variant='text-input-field-r'
          >
            Custom credits
          </Typography>
        )}
        {!plan.showCustomCredits && (
          <Select
            variant='stroke'
            items={plan.selectItems}
            value={selectedValue}
            dropdownProps={{
              withSearch: false,
            }}
            textFieldPlaceholderProps={{
              className: styles.selectPlan,
            }}
            onSelectValue={onSelectValue}
          />
        )}
        <Button text='Select plan' size='large' fullWidth {...plan.buttonProps} />
      </div>

      <div className={styles.includesData}>
        <Typography
          className={styles.includesDataTitle}
          ariaLabel='includes_data_title'
          variant='body-md-semibold'
        >
          Includes:
        </Typography>
        <div className={styles.includesDataLine}>
          <Icon icon='contacts' color={plan.iconColor} />
          <Typography
            className={styles.includesDataLineText}
            ariaLabel='includes_data_line_text'
            variant='body-md-regular'
          >
            {plan.isCustom ? 'Custom seats' : '1 seat'}
          </Typography>
          {plan.showTooltip && (
            <Tooltip
              type='description'
              label={<SeatPlanIncludesTooltip additionalSeatPrice={additionalSeatPrice} />}
              placement='top'
            >
              <Icon icon='lineInfoCircle' fontSize={12} color='var(--content-primary-tertiary)' />
            </Tooltip>
          )}
        </div>
        <div className={styles.includesDataLine}>
          <Icon icon='phone' color={plan.iconColor} />
          <Typography
            className={styles.includesDataLineText}
            ariaLabel='includes_data_line_text'
            variant='body-md-regular'
          >
            {plan.isCustom ? 'Custom numbers' : '1 number'}
          </Typography>
          {plan.showTooltip && (
            <Tooltip
              type='description'
              label={<NumberPlanIncludesTooltip additionalNumberPrice={additionalNumberPrice} />}
              placement='top'
            >
              <Icon icon='lineInfoCircle' fontSize={12} color='var(--content-primary-tertiary)' />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
